import { HealthStatusEntry } from '../model/healthStatus';
import { TkvAngabenV2Item } from '../reducer/tkvAngabenV2.reducer';
import type { IAppState } from '../localStore';
import { StatePreV28, StateV28 } from './28_migrate_diagnosis_codes.interface';

// PS20-141217: TKV2.X healthStatus codes for these diagnoses were re-grouped
// (e.g. generic "KI001"/"HI001" split into bacterial/viral "AI001"/"AI002", or
// replaced by a more specific code). Keyed by diagnosis label + the exact old
// code, so entries are only touched when both match - no cross-species or
// cross-diagnosis ambiguity even though several diagnoses shared the same old code.
const DIAGNOSIS_CODE_CORRECTIONS: Record<string, Record<string, string>> = {
  Anaplasmose: { KI001: 'AI001', HI001: 'AI001' },
  Borreliose: { KI001: 'AI001', HI001: 'AI001' },
  Ehrlichiose: { KI001: 'AI001', HI001: 'AI001' },
  'FeLV (Felines Leukämievirus)': { KI002: 'KI005' },
  'FIP (Feline infektiöse Peritonitis)': { KI001: 'AI002' },
  'FIV (Felines Immundefizienz-Virus)': { KI002: 'KI005' },
  Herpes: { KI001: 'AI002', HI001: 'AI002' },
  Influenza: { KI003: 'KI007' },
  Leptospirose: { KI001: 'AI001', HI002: 'AI001' },
  Staupe: { KI001: 'AI002' },
  Tetanus: { KI001: 'AI001', HI001: 'AI001' },
  Toxoplasmose: { KI001: 'KI008' },
  Parvovirose: { HI001: 'AI002' },
};

// Saved state can be years old; normalize to NFC before the lookup so a diagnosis
// label persisted with a different Unicode normalization (e.g. decomposed "a" +
// combining diaeresis instead of the precomposed "ä") still matches the map keys.
const findCorrections = (diagnosis: string): Record<string, string> | undefined => {
  const normalized = diagnosis.normalize('NFC');
  const directMatch = DIAGNOSIS_CODE_CORRECTIONS[normalized];
  if (directMatch) {
    return directMatch;
  }
  const key = Object.keys(DIAGNOSIS_CODE_CORRECTIONS).find(
    candidate => candidate.normalize('NFC') === normalized,
  );
  return key ? DIAGNOSIS_CODE_CORRECTIONS[key] : undefined;
};

const DIAGNOSIS_LABEL_RENAMES: Record<string, { diagnosis: string; notInsuredText: string }> = {
  'Zahnbehandlung (unspezifisch)': {
    diagnosis: 'Zahnproblematik (unspezifisch)',
    notInsuredText:
      'Behandlungen des betroffenen Zahns, der direkt angrenzenden Zähne und des umliegenden Gewebes',
  },
};

const findRename = (diagnosis: string): { diagnosis: string; notInsuredText: string } | undefined => {
  const normalized = diagnosis.normalize('NFC');
  const directMatch = DIAGNOSIS_LABEL_RENAMES[normalized];
  if (directMatch) {
    return directMatch;
  }
  const key = Object.keys(DIAGNOSIS_LABEL_RENAMES).find(candidate => candidate.normalize('NFC') === normalized);
  return key ? DIAGNOSIS_LABEL_RENAMES[key] : undefined;
};

const renameDiagnosisLabel = (entry: HealthStatusEntry): HealthStatusEntry => {
  if (!entry.diagnosis) {
    return entry;
  }
  const rename = findRename(entry.diagnosis);
  return rename ? { ...entry, diagnosis: rename.diagnosis, notInsuredText: rename.notInsuredText } : entry;
};

const correctNotInsuredScopes = (
  diagnosis: string | null,
  notInsuredScopes: HealthStatusEntry['notInsuredScopes'],
): HealthStatusEntry['notInsuredScopes'] => {
  if (!diagnosis || !notInsuredScopes) {
    return notInsuredScopes;
  }
  const corrections = findCorrections(diagnosis);
  if (!corrections) {
    return notInsuredScopes;
  }
  return notInsuredScopes.map(scope =>
    scope.code && corrections[scope.code] ? { ...scope, code: corrections[scope.code] } : scope,
  );
};

const migrateHealthStatusEntries = (entries: HealthStatusEntry[] | undefined): HealthStatusEntry[] | undefined => {
  return entries?.map(entry => ({
    ...renameDiagnosisLabel(entry),
    notInsuredScopes: correctNotInsuredScopes(entry.diagnosis, entry.notInsuredScopes),
  }));
};

const migrateAngabenItems = (items: TkvAngabenV2Item[]): TkvAngabenV2Item[] => {
  return items?.map(item => ({
    ...item,
    healthStatusEntries: migrateHealthStatusEntries(item.healthStatusEntries),
    previousHealthStatusEntries: migrateHealthStatusEntries(item.previousHealthStatusEntries),
  }));
};

// The AI001/AI002/... codes only exist in the backend's v2 healthStatus dataset, which is
// served only when its TKV_HEALTHSTATUS toggle is active (PS20-141217). Remapping
// unconditionally would produce codes the backend's default (legacy) dataset doesn't
// recognize for every saved offer outside devnext/uatnext, so this only runs when the live
// toggle (passed in via the generic migrator `context`, same state shape as selectFeatureToggles) is on.
export const migrateDiagnosisCodes = (
  previousState: StatePreV28,
  newVersion: number,
  context?: IAppState,
): StateV28 => {
  const useV2HealthStatusCodes = context?.transientState?.featureToggles?.['TKV_HEALTHSTATUS'] === true;
  return {
    ...previousState,
    version: newVersion,
    tkvAngabenV2: previousState.tkvAngabenV2 && useV2HealthStatusCodes
      ? {
          ...previousState.tkvAngabenV2,
          angabenItems: migrateAngabenItems(previousState.tkvAngabenV2.angabenItems),
        }
      : previousState.tkvAngabenV2,
  };
};
